import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useAccountList } from '@features/account'
import { useAuthStore } from '@features/auth'
import { ConfirmModal, Spinner } from '@shared/ui'

const getAccountPath = (pathname: string) =>
  pathname.startsWith('/mypage') ? '/mypage/account' : '/account'

const UNAUTHENTICATED_REDIRECT_PATH = '/'
const AUTH_ONLY_PATHS = new Set(['/account', '/mypage/account', '/delete-user', '/mypage/delete-user'])

// 비로그인 전용 (로그인 상태면 홈으로)
export const GuestGuard = () => {
  const isLoggedIn = useAuthStore(s => s.isLoggedIn)
  if (isLoggedIn) return <Navigate to="/" replace />
  return <Outlet />
}

// 로그인 필수 (로그인 안 됐으면 /login으로)
export const AuthGuard = () => {
  const isLoggedIn = useAuthStore(s => s.isLoggedIn)
  const { pathname } = useLocation()
  const { isAccountReady, isLoading } = useAccountList()
  if (!isLoggedIn) return <Navigate to={UNAUTHENTICATED_REDIRECT_PATH} replace />
  if (AUTH_ONLY_PATHS.has(pathname)) return <Outlet />
  const accountPath = getAccountPath(pathname)
  if (isLoading) return <Spinner size="lg" fullscreen />
  if (!isAccountReady) {
    return <Navigate to={accountPath} replace />
  }
  return <Outlet />
}

// 로그인 + 어카운트 status 필수 (로그인 안 됐으면 /login, status가 1(생성완료)이 아니면 어카운트 등록 페이지로)
// status는 useAccountList 조회 시 localStorage에 저장됨 — 미조회/미생성/정지 상태면 모두 redirectTo로 보냄
// redirectTo — 어카운트 페이지 경로. 모바일 /mypage/account, PC /account
export const SvrGuard = ({ redirectTo = '/mypage/account' }: { redirectTo?: string }) => {
  const isLoggedIn = useAuthStore(s => s.isLoggedIn)
  const { isAccountReady, isLoading } = useAccountList()
  if (!isLoggedIn) return <Navigate to={UNAUTHENTICATED_REDIRECT_PATH} replace />
  if (isLoading) return <Spinner size="lg" fullscreen />
  if (!isAccountReady) return <Navigate to={redirectTo} replace />
  return <Outlet />
}

export const MobileAccountRequiredGuard = () => {
  const navigate = useNavigate()
  const isLoggedIn = useAuthStore(s => s.isLoggedIn)
  const svrId = useAuthStore(s => s.svrId)
  const hasRegisteredAccount = !!svrId
  const { isAccountReady, isLoading } = useAccountList({ enabled: hasRegisteredAccount })

  if (!isLoggedIn) return <Navigate to={UNAUTHENTICATED_REDIRECT_PATH} replace />

  if (!hasRegisteredAccount) {
    return (
      <AccountRequiredModal
        onConfirm={() => navigate('/mypage/account', { replace: true })}
        onCancel={() => navigate('/mypage', { replace: true })}
      />
    )
  }

  if (isLoading) return <Spinner size="lg" fullscreen />

  if (!isAccountReady) {
    return (
      <AccountRequiredModal
        onConfirm={() => navigate('/mypage/account', { replace: true })}
        onCancel={() => navigate('/mypage', { replace: true })}
      />
    )
  }

  return <Outlet />
}

const AccountRequiredModal = ({
  onConfirm,
  onCancel,
}: {
  onConfirm: () => void
  onCancel: () => void
}) => (
  <div className="min-h-screen bg-[#1b1c1d]">
    <ConfirmModal
      isOpen
      title="해당 서비스는 account 등록이 필요합니다. 이동하시겠습니까?"
      confirmLabel="이동"
      cancelLabel="닫기"
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  </div>
)
